import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BikletaLogoComplete, loginSlidesImages } from '../../assets';
import { useSlideshow } from '../../utils/useSlideshow';
import { useApplicationController } from './application.controller';
import BenefitStep from './steps/BenefitStep';
import CommerceStep from './steps/CommerceStep';
import './application.css';

const STEPS = ['Tu comercio', 'Tu beneficio'];

/** Los mismos labels que muestra la tarjeta del beneficio en la app. */
const BENEFIT_TYPE_LABELS: Record<string, string> = {
  discount: 'Descuento',
  promo: 'Promoción',
  exchange: 'Canje',
};

/** Flecha de volver. Funcional, no decorativa: el único ícono decorativo sigue siendo ↘. */
const BackArrow: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M12.5 4 6.5 10l6 6" />
  </svg>
);

const CommerceApplicationView: React.FC = () => {
  const controller = useApplicationController();
  const { formik, step } = controller;
  const navigate = useNavigate();
  /** Las mismas fotos que rotan en el login, para que el alta se lea como su continuación. */
  const slide = useSlideshow(loginSlidesImages.length);

  const isStep1 = step === 0;
  const missing = controller.stepErrors.length;

  /*
   * Desde el paso 1 la flecha sale del alta hacia el login, así que pregunta antes si ya
   * hay algo cargado: es el único punto del proceso donde volver descarta datos, y el
   * requisito es justamente que la flecha no cierre el registro sin querer.
   */
  const goBack = () => {
    if (!isStep1) {
      controller.goBack();
      return;
    }

    if (
      formik.dirty &&
      !window.confirm(
        '¿Salir del registro? Se va a perder lo que cargaste hasta acá.'
      )
    ) {
      return;
    }

    navigate('/auth/signin');
  };

  return (
    <div className="bk-app">
      <div className="bk-panel">
        <div className="bk-panel__inner">
          {/*
            La flecha va siempre primero y en la misma posición —arriba a la izquierda,
            antes del título— en los dos pasos y en los dos tamaños de pantalla. El logo
            pasó a la derecha para dejarle esa esquina.
          */}
          <header className="bk-top">
            <button
              type="button"
              className="bk-back"
              disabled={controller.isSubmitting}
              onClick={goBack}
              aria-label={isStep1 ? 'Volver al ingreso' : 'Volver a tu comercio'}
            >
              <BackArrow />
            </button>

            {isStep1 && (
              <span className="bk-top__link">
                ¿Ya tienes una cuenta? <Link to="/auth/signin">Ingresar</Link>
              </span>
            )}

            {/* `.light` es el trazo blanco: el panel es navy. */}
            <img
              className="bk-logo"
              src={BikletaLogoComplete.light}
              alt="Bikleta"
            />
          </header>

          <div>
            <h1 className="bk-h1">
              {isStep1 ? 'Crea tu cuenta' : 'Crea tu beneficio'}
            </h1>
            <p className="bk-lead">
              {isStep1
                ? 'Registra tu comercio aliado en Bikleta y administra tus beneficios desde un solo lugar.'
                : 'Define qué ofrecerás a la comunidad ciclista y cómo se verá en la app. Podrás editarlo cuando quieras desde tu panel.'}
            </p>
          </div>

          <div className="bk-stepper">
            {STEPS.map((label, index) => (
              <div key={label}>
                <div
                  className={`bk-step__bar${
                    index === step
                      ? ' bk-step__bar--active'
                      : index < step
                      ? ' bk-step__bar--done'
                      : ''
                  }`}
                />
                <p
                  className={`bk-step__label${
                    index === step ? ' bk-step__label--active' : ''
                  }`}
                >
                  {index < step ? '✓ ' : ''}
                  {label}
                </p>
              </div>
            ))}
          </div>

          <form onSubmit={(event) => event.preventDefault()}>
            {isStep1 ? (
              <CommerceStep controller={controller} />
            ) : (
              <BenefitStep controller={controller} />
            )}
          </form>

          {/*
            La especificación reemplazó el resumen en tarjeta por foco en el primer campo
            inválido. Esta línea queda para que quien ve la pantalla sepa cuántos faltan,
            y es `role="alert"` para que el lector de pantalla lo anuncie.
          */}
          {missing > 0 && (
            <p className="bk-alert" role="alert">
              {missing === 1 ? 'Falta 1 dato' : `Faltan ${missing} datos`}. Revisa
              lo que está marcado.
            </p>
          )}

          <div className="bk-actions">
            <button
              type="button"
              className={`bk-btn${
                controller.isSubmitting ? ' bk-btn--loading' : ''
              }${!controller.isStepComplete ? ' bk-btn--incomplete' : ''}`}
              disabled={controller.isSubmitting}
              onClick={isStep1 ? controller.goNext : controller.submit}
            >
              {controller.isSubmitting
                ? controller.uploadStatus ?? 'Enviando…'
                : !controller.isStepComplete
                ? `${isStep1 ? 'Continuar' : 'Registrar mi comercio'} · faltan datos`
                : `${isStep1 ? 'Continuar' : 'Registrar mi comercio'} ↘`}
            </button>
          </div>
        </div>
      </div>

      <aside
        className={`bk-aside ${
          isStep1 ? 'bk-aside--photo' : 'bk-aside--preview'
        }`}
      >
        {isStep1 ? (
          // Mismas fotos que el login y con el mismo fundido, para que el registro se lea
          // como su continuación. Son decorativas: no llevan `alt`.
          <div className="bk-slides" aria-hidden="true">
            {loginSlidesImages.map((image, index) => (
              <img
                key={index}
                className={`bk-slide${index === slide ? ' bk-slide--on' : ''}`}
                src={image}
                alt=""
              />
            ))}
            <span className="bk-slides__vignette" />
          </div>
        ) : (
          <div className="bk-aside__preview">
            <p className="bk-aside__title">Así se ve en la app</p>
            {/* Misma distribución que la tarjeta "Home" de `BenefitPreviewer`, el
                previsualizador que ya usa el panel de comercios. */}
            <div className="bk-preview">
              <div className="bk-preview__photo">
                {controller.benefitPreviewUrl ? (
                  <img
                    src={controller.benefitPreviewUrl}
                    alt="Vista previa del beneficio"
                  />
                ) : (
                  <span>Sube una foto para ver la vista previa</span>
                )}
                {formik.values.benefitTag && (
                  <span className="bk-preview__tag">
                    {formik.values.benefitTag}
                  </span>
                )}
              </div>
              <div className="bk-preview__body">
                <p className="bk-preview__title">
                  {formik.values.benefitTitle || 'Título de tu beneficio'}
                </p>
                <p className="bk-preview__type">
                  {BENEFIT_TYPE_LABELS[formik.values.benefitType] ??
                    'Tipo de beneficio'}
                </p>
                {/*
                  La descripción sólo aparece en la tarjeta de detalle de la app, no en
                  la del listado que replica esta vista previa. Se muestra igual porque
                  es el campo más largo que escribe el comercio y sin esto no tenía
                  ningún reflejo de lo que estaba cargando.
                */}
                <p className="bk-preview__description">
                  {formik.values.benefitDescription ||
                    'Acá va la descripción que escribas.'}
                </p>
              </div>
            </div>
            <p className="bk-aside__foot">
              La vista previa se actualiza mientras escribes.
            </p>
          </div>
        )}
      </aside>
    </div>
  );
};

export default CommerceApplicationView;
